/**
 * Hierarchy building + outline numbering, extracted from `HierarchyTree` so the
 * exact same sort/nesting rules can be unit-tested and reused outside the
 * component (the folder-layouts detail pane shows the number of the selected
 * folder and must agree with the tree).
 *
 * Numbering is **derived, never persisted**: it is a presentation of each
 * node's position among its siblings in the *rendered* order.
 */

export const ROOT_KEY = '__root__'

/** Minimal shape the hierarchy helpers need. */
export interface OutlineNode {
    uid: string
    label: string
    parentUid?: string | null
    /** Only meaningful when `orderable`; ignored otherwise. */
    ordering?: number
}

export type OutlineTreeNode<T extends OutlineNode> = T & {
    children: OutlineTreeNode<T>[]
    level: number
}

export function parentKeyOf(parentUid: string | null | undefined): string {
    return parentUid === null || parentUid === undefined || parentUid === '' ? ROOT_KEY : parentUid
}

/**
 * Children indexed by parent key, sorted the way they are rendered: by
 * `ordering` then label when `orderable`, alphabetically otherwise.
 */
export function groupChildrenByParent<T extends OutlineNode>(
    nodes: T[],
    orderable: boolean
): Map<string, T[]> {
    const map = new Map<string, T[]>()
    for (const node of nodes) {
        const key = parentKeyOf(node.parentUid)
        const list = map.get(key)
        if (list) list.push(node)
        else map.set(key, [node])
    }
    for (const list of map.values()) {
        list.sort((a, b) => {
            if (orderable) {
                const diff = (a.ordering ?? 0) - (b.ordering ?? 0)
                if (diff !== 0) return diff
                // Ties must NOT fall through to the label: alphabetical is not
                // the order the user dragged, and it would silently disagree
                // with the server, which breaks ties on `id`. `uid` is the only
                // total, stable key both sides can agree on, so the tree renders
                // the same on every load. Live data is duplicate-free (the
                // server densifies each sibling set per write) — this covers
                // legacy rows and the window between an optimistic move and the
                // refetch.
                return a.uid.localeCompare(b.uid)
            }
            return a.label.localeCompare(b.label)
        })
    }
    return map
}

/**
 * Roots first, then nodes whose parent no longer exists — they would otherwise
 * vanish, so they are surfaced at root level instead of being silently dropped.
 */
export function buildHierarchy<T extends OutlineNode>(
    nodes: T[],
    childrenByParent: Map<string, T[]>
): OutlineTreeNode<T>[] {
    const knownUids = new Set(nodes.map(n => n.uid))
    const build = (parentKey: string, level: number, seen: Set<string>): OutlineTreeNode<T>[] => {
        const list = childrenByParent.get(parentKey) || []
        return list
            .filter(node => !seen.has(node.uid))
            .map(node => {
                const nextSeen = new Set(seen)
                nextSeen.add(node.uid)
                return { ...node, level, children: build(node.uid, level + 1, nextSeen) }
            })
    }
    const roots = build(ROOT_KEY, 0, new Set())
    const orphans = nodes
        .filter(n => {
            const key = parentKeyOf(n.parentUid)
            return key !== ROOT_KEY && !knownUids.has(key)
        })
        .map(n => ({ ...n, level: 0, children: build(n.uid, 1, new Set([n.uid])) }))
    return [...roots, ...orphans]
}

/**
 * Outline number of every node of an already-built hierarchy: `1`, `1.1`,
 * `1.1.1`, `1.2`, `2` … following the rendered sibling order.
 */
export function numberHierarchy<T extends OutlineNode>(
    roots: OutlineTreeNode<T>[]
): Map<string, string> {
    const numbers = new Map<string, string>()
    const walk = (siblings: OutlineTreeNode<T>[], prefix: string) => {
        siblings.forEach((node, index) => {
            const number = prefix ? `${prefix}.${index + 1}` : String(index + 1)
            numbers.set(node.uid, number)
            if (node.children.length > 0) walk(node.children, number)
        })
    }
    walk(roots, '')
    return numbers
}

/**
 * Outline number of every node, keyed by uid. `orderable` must match what the
 * tree renders with, otherwise the numbers would not follow the visible order.
 */
export function computeOutlineNumbers<T extends OutlineNode>(
    nodes: T[],
    orderable = false
): Map<string, string> {
    return numberHierarchy(buildHierarchy(nodes, groupChildrenByParent(nodes, orderable)))
}
